import { useCallback } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import {
  useBoardElementRectStore,
  useWallContentStore,
  useWallStore,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { autoAlignGap } from '../../../board/positioning/autoAlign';
import { updateCardOrClusterPosition } from '../../../board/positioning/persistence';
import { getOrderPageCardPositionUpdates } from '../../../board/positioning/orderPageCards';
import { isClusterCard } from '../../../board/selectors/boardCardSelectors';

const orderGap = autoAlignGap;
const orderBoundaryMargin = 100;

export function useOrderPageCards(board: WallBoard) {
  const cards = useWallContentStore((state) => state.cards);
  const measuredRects = useBoardElementRectStore((state) => state.rects);
  const updateCard = useWallContentStore((state) => state.updateCard);
  const topicKey = useWallStore((state) => state.topicKey);

  return useCallback(() => {
    if (!board.isOwner) {
      return;
    }

    const viewport = document.getElementById('topic-cards');

    if (!viewport) {
      return;
    }

    const updates = getOrderPageCardPositionUpdates(
      cards,
      {
        height: Math.max(0, viewport.clientHeight - orderBoundaryMargin * 2),
        width: Math.max(0, viewport.clientWidth - orderBoundaryMargin * 2),
        x: orderBoundaryMargin,
        y: orderBoundaryMargin,
      },
      { gap: orderGap, measuredRects }
    );

    updates.forEach(({ cardId, xPos, yPos }) => {
      updateCard(cardId, { xPos, yPos });
      void updateCardOrClusterPosition(board.id, cards, cardId, xPos, yPos, topicKey);
      publishBoardSocketPackage({
        action: isClusterCardId(cards, cardId) ? 'updateClusterPosition' : 'updateCardPosition',
        topic: board.id,
        position: { id: cardId, xPos, yPos },
      });
    });
  }, [board.id, board.isOwner, cards, measuredRects, topicKey, updateCard]);
}

function isClusterCardId(cards: WallCard[], cardId: string): boolean {
  const card = findCard(cards, cardId);

  return Boolean(card && isClusterCard(card));
}

function findCard(cards: WallCard[], cardId: string): WallCard | null {
  for (const card of cards) {
    if (card.id === cardId) {
      return card;
    }

    const childCard = findCard(card.cards, cardId);

    if (childCard) {
      return childCard;
    }
  }

  return null;
}
